import _ from "lodash";

import { call, takeEvery, fork, delay } from "redux-saga/effects";
import { UAParser } from "ua-parser-js";
import ms from "ms";
import type { NormalizedCustomerDto } from "helpers/normalizeCustomerDto";
import type { AnyAction } from "@reduxjs/toolkit";
import ApiClient, { API_CLIENT_ACTIONS } from "services/ApiClient";
import ApiClientFrontLogs from "services/ApiClientFrontLogs";
import getClientUniqueId from "helpers/getClientUniqueId";

import ErrorLoggerService from "core/ErrorLoggerService";

import HEADERS from "../constants/HEADERS";
import COUNTRY from "../constants/COUNTRY";
import store from "../store/configureStore";

interface LogObjectType {
  deviceId: string;
  sessionHash: string;
  application: string;
  customerId: string;
  email: string;
  mobilePhone: string;
  userAgent: UAParser.IResult;
  actions: string[];
  firstInit?: boolean;
  update?: boolean;
}

interface ResponseType {
  data: string;
}

const FIRST_DELAY = ms("2sec");
const TIME_BETWEEN_LOG_POSTS = ms("5sec");
const TRY_REQUEST_INTERVAL = ms("5sec");

// Handle synchronous errors
window.addEventListener("error", (event: ErrorEvent) => {
  try {
    store.dispatch({
      type: "##browser/ERROR",
      errorType: "error",
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error?.toString() || "Unknown error"
    });
  }
  catch (err) {
    ErrorLoggerService(
      "clientLoggerSaga",
      "Failed to dispatch error event:",
      err
    );
  }
});

// Handle unhandled promise rejections
window.addEventListener(
  "unhandledrejection",
  (event: PromiseRejectionEvent) => {
    try {
      store.dispatch({
        type: "##browser/ERROR",
        errorType: "unhandledRejection",
        reason: event.reason?.toString?.() || String(event.reason),
        error:
          event.reason instanceof Error ?
            event.reason.toString() :
            "Unknown rejection"
      });
    }
    catch (err) {
      ErrorLoggerService(
        "clientLoggerSaga",
        "Failed to dispatch unhandled rejection event:",
        err
      );
    }
  }
);

const generateSessionHash = () =>
  _.times(3, () => Math.random().toString(32)
    .split(".")[1]).join(".");

const deviceId = getClientUniqueId();
const userAgent = UAParser(window.navigator.userAgent);
const sessionHash = generateSessionHash();

ApiClient.setHeader(HEADERS.sessionId, sessionHash);

let logObject: LogObjectType = {
  deviceId,
  sessionHash,
  application: COUNTRY.application,
  customerId: "",
  email: "",
  mobilePhone: "",
  userAgent,
  actions: []
};

export function assignLoggerVariables(
  customerDetails: NormalizedCustomerDto,
  isReset?: boolean
) {
  const email = customerDetails?.email;
  const mobilePhone = customerDetails?.phone;
  const customerId = customerDetails?.id;

  logObject = Object.assign(
    {},
    logObject,
    {
      update: true
    },
    {
      email,
      mobilePhone,
      customerId
    },
    isReset ?
      {
        sessionHash: generateSessionHash(),
        firstInit: true
      } :
      {}
  );
}

const processRequestsOrResponses = (action: AnyAction) => {
  try {
    const isActionRequestOrResponse = [
      API_CLIENT_ACTIONS.REQUEST_SENT,
      API_CLIENT_ACTIONS.RESPONSE_RECEIVED
    ].includes(action.type);

    if (isActionRequestOrResponse) {
      const {
        type,
        payload: { cancelled, networkError, url, status, message, headers }
      } = action;
      const msg = _.defaultTo(
        message,
        cancelled ? "Timeout" : networkError ? "Network Error" : ""
      );
      return {
        type,
        url: _.get(action, "payload.config.url", url),
        status,
        message: msg,
        correlationId: _.get(headers, "correlationid")
      };
    }
    else {
      return action;
    }
  }
  catch (err) {
    ErrorLoggerService(
      "clientLoggerSaga",
      "Error in processRequestsOrResponses",
      err
    );
  }
};

const checkProp = (prop: unknown) =>
  _.isString(prop) || _.isNumber(prop) || _.isBoolean(prop);

const normalizeAction = (action: AnyAction) =>
  _.mapValues(action, (val) => {
    if (checkProp(val)) {
      return val;
    }
    else if (_.isObject(val)) {
      return _.pickBy(val, checkProp);
    }
    else {
      return undefined;
    }
  });

function* watchAndAddNewActionsToStack() {
  yield takeEvery("*", (actionFromDispatch: AnyAction) => {
    const modifiedAction = {
      type: actionFromDispatch.type,
      activePage: _.last(_.split(window.location.href, window.location.origin)),
      actionTime: new Date().toISOString(),
      actionPayload: normalizeAction(
        _.omit(
          processRequestsOrResponses(actionFromDispatch),
          "type"
        ) as AnyAction
      )
    };
    let stringifiedAction = null;
    try {
      stringifiedAction = JSON.stringify(modifiedAction);
    }
    catch (error) {
      stringifiedAction = "JSON stringify error";
    }
    logObject = Object.assign({}, logObject, {
      actions: [ ...logObject.actions, stringifiedAction ]
    });
  });
}

export function* sendLogs(
  oldActions: any[] = [],
  firstInit: boolean = false
): Generator<any, void, any> {
  try {
    if (_.isEmpty(logObject.actions) && _.isEmpty(oldActions)) {
      yield delay(TRY_REQUEST_INTERVAL);
      yield call(sendLogs, [], firstInit);
      return;
    }

    const dataToSend = {
      ...logObject,
      firstInit: logObject.firstInit || firstInit,
      update: firstInit ? false : logObject.update,
      actions: [ ...oldActions, ...logObject.actions ]
    };
    logObject.actions = [];
    logObject.firstInit = false;
    logObject.update = false;

    const { data } = yield call(ApiClientFrontLogs.request<ResponseType>, {
      method: dataToSend.firstInit ? "post" : "put",
      url: "/logs/write",
      data: dataToSend
    });
    if (data) {
      yield delay(TIME_BETWEEN_LOG_POSTS);
      yield call(sendLogs);
    }
    else {
      yield delay(TRY_REQUEST_INTERVAL);
      yield call(sendLogs, dataToSend.actions, firstInit);
    }
  }
  catch (err) {
    ErrorLoggerService("clientLoggerSaga", "Error in sendLogs", err);
  }
}

export default function* sagaWatcher() {
  try {
    const isProductionEnv = import.meta.env.PROD && import.meta.env.MODE === "production";
    if (isProductionEnv) {
      yield fork(watchAndAddNewActionsToStack);
      yield delay(FIRST_DELAY);
      yield call(sendLogs, [], true);
    }
  }
  catch (err) {
    ErrorLoggerService("clientLoggerSaga", "Error in sagaWatcher", err);
  }
}
